import {getDocumentMaxFiles} from '@sollapay/domain';
import {ASSET_FIELD_KEYS} from '@sollapay/domain/asset-types';
import {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import {FieldMoneyInput, FormSectionHeading} from '@sollapay/ui/components';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FormShell, LOCKED_FLEX_COL_CLASSNAME, ReadOnlyFieldset} from '@/components';
import {
  AiExtractionStatus,
  AiFlashWrapper,
  useAiFillFlash,
  useFieldAiProps
} from '@/features/ai-extraction';
import {AssetAttachmentsFields, AssetDescriptorFields} from '@/features/buyers/components';
import {generatePurchasePriceNis, withDebugFieldChange} from '@/features/debug-test-data';
import {
  DocumentsAutoFillTip,
  DocumentUploadTabs,
  getDocumentTypeLabel,
  resolveDocumentError
} from '@/features/documents';
import {useShowErrors} from '@/features/trust-accounts/activation/hooks';
import {resolveFieldError} from '@/features/trust-accounts/activation/utils';

import {useAssetDetailsForm} from '../hooks';

import type {DebugSetValue} from '@/features/debug-test-data';
import type {FormStateSnapshot} from '@/hooks';
import type {AssetType} from '@sollapay/enums';
import type {AssetDTO} from '@sollapay/types';
import type {FC} from 'react';

interface AssetDetailsStepFormProps {
  trustAccountId: string;
  asset: AssetDTO | null | undefined;
  isLoading: boolean;
  isError: boolean;
  readOnly?: boolean;
  documentsReadOnly?: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

const i18nPrefix = 'activation.packages.trustDetails.assetDetails';

/** Optional documents that are "required unless annexed": an entry (i18n key under `i18nPrefix`)
 * gives the tab an asterisk plus an explaining tooltip, while the upload stays unenforced.
 * Only ever consulted for optional tabs (see `documentTabs` below) — a required tab that happens
 * to share a `DocumentType` with an entry here must never pick up its tooltip. */
const CONDITIONALLY_REQUIRED_DOCUMENT_TOOLTIP_KEYS: Partial<Record<DocumentType, string>> = {
  [DocumentType.SALE_AGREEMENT]: 'saleAgreementTooltip'
};

export const AssetDetailsStepForm: FC<AssetDetailsStepFormProps> = ({
  trustAccountId,
  asset,
  isLoading,
  isError,
  readOnly = false,
  documentsReadOnly,
  onFormStateChange
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const showErrors = useShowErrors();
  const {
    form,
    handleSave,
    saveError,
    fieldErrors,
    allowedAssetTypes,
    propertyDocumentTypes,
    optionalPropertyDocumentTypes,
    missingDocumentTypes,
    aiFields,
    isProcessing,
    suggestionsCount,
    markFieldModified,
    stopAllScopes
  } = useAssetDetailsForm({
    trustAccountId,
    asset,
    isLoading,
    isError,
    isReadOnly: readOnly,
    onFormStateChange
  });
  const {control, register, setValue} = form;
  const setDebugValue = setValue as DebugSetValue;

  const fieldAi = useFieldAiProps({
    aiFields,
    isProcessing,
    markFieldModified,
    getValue: form.getValues
  });
  const isFlashing = useAiFillFlash(aiFields);

  const fe = (key: string) => resolveFieldError(showErrors, fieldErrors, t, key);

  const assetType = useWatch({control, name: 'assetType'});

  const handleAssetTypeChange = (next: AssetType) => {
    setValue('assetType', next, {shouldDirty: true});
    ASSET_FIELD_KEYS.forEach(key => setValue(key, '', {shouldDirty: true}));
  };

  const documentTabs = [
    ...propertyDocumentTypes.map(documentType => ({documentType, enforced: true})),
    ...optionalPropertyDocumentTypes.map(documentType => ({documentType, enforced: false}))
  ].map(({documentType, enforced}) => {
    const tooltipKey = enforced
      ? undefined
      : CONDITIONALLY_REQUIRED_DOCUMENT_TOOLTIP_KEYS[documentType];
    return {
      value: documentType,
      label: getDocumentTypeLabel(documentType, t),
      documentType,
      maxFiles: getDocumentMaxFiles(documentType),
      required: enforced || !!tooltipKey,
      error: enforced
        ? resolveDocumentError(documentType, missingDocumentTypes, showErrors, t)
        : undefined,
      tooltip: tooltipKey ? t(`${i18nPrefix}.${tooltipKey}`) : undefined
    };
  });

  // No field here carries an RHF-level rule (this is an autosave step — see `enforceFieldRules`
  // below), so `assetType`'s live-computed error is the only thing this ever needs to surface.
  const assetTypeError = fe('assetType');
  const descriptorErrors = assetTypeError ? {assetType: {message: assetTypeError}} : {};

  return (
    <FormShell
      id="asset-details-form"
      className="flex flex-col gap-6"
      onSubmit={handleSave}
      saveError={saveError}
    >
      <FormSectionHeading
        title={t(`${i18nPrefix}.title`)}
        description={t(`${i18nPrefix}.description`)}
      />

      {documentTabs.length > 0 && (
        <>
          <DocumentsAutoFillTip />
          <DocumentUploadTabs
            entityType={DocumentEntityType.TRUST_ACCOUNT}
            entityId={trustAccountId}
            documentContext={DocumentContext.TRUST_DETAILS}
            readOnly={documentsReadOnly}
            tabs={documentTabs}
          />
        </>
      )}

      <ReadOnlyFieldset readOnly={readOnly} lockedClassName={LOCKED_FLEX_COL_CLASSNAME}>
        <AiExtractionStatus
          isProcessing={isProcessing}
          suggestionsCount={suggestionsCount}
          onFillManually={stopAllScopes}
        />

        <AssetDescriptorFields
          control={control}
          register={register}
          errors={descriptorErrors}
          namePrefix=""
          assetType={assetType}
          allowedAssetTypes={allowedAssetTypes}
          onAssetTypeChange={handleAssetTypeChange}
          i18nPrefix={i18nPrefix}
          additionalDetailsDescription={t(`${i18nPrefix}.additionalDetailsDescription`)}
          enforceFieldRules={false}
          ai={{fieldAi, isFlashing}}
          debug={{setDebugValue}}
          fullWidthField={
            <AiFlashWrapper field="referencePriceNis" fieldAi={fieldAi} isFlashing={isFlashing}>
              {ai => (
                <Controller
                  name="referencePriceNis"
                  control={control}
                  render={({field}) => (
                    <FieldMoneyInput
                      label={t(`${i18nPrefix}.referencePriceNis`)}
                      placeholder={t(`${i18nPrefix}.referencePriceNisPlaceholder`)}
                      required
                      error={fe('referencePriceNis')}
                      value={field.value}
                      onChange={value => {
                        field.onChange(value);
                        ai.onAiChange();
                      }}
                      onBlur={field.onBlur}
                      cornerBadge={withDebugFieldChange(
                        ai.aiIndicator,
                        field.onChange,
                        generatePurchasePriceNis,
                        ai.onAiChange
                      )}
                      isLoading={ai.aiGenerating}
                    />
                  )}
                />
              )}
            </AiFlashWrapper>
          }
        />

        <AssetAttachmentsFields
          control={control}
          register={register}
          errors={{}}
          namePrefix="attachments"
          ai={{fieldAi, isFlashing, aiFields, getValues: form.getValues}}
        />
      </ReadOnlyFieldset>
    </FormShell>
  );
};
